import { Suspense } from 'react';

import { Result, Space, Spin } from 'antd';
import { Navigate, Outlet, useLocation } from 'react-router-dom';

import { ROUTES } from '@/config/routes';
import { AutomationRoutes } from '@/features/automation/layouts/routes';
import { CalendarRoutes } from '@/features/calendar/layouts/routes';
import { CmsRoutes } from '@/features/cms/routes';
import { EmailRoutes } from '@/features/email/routes';
import { useMyCompanies } from '@/features/login/api/getMyCompanies';
import { NotAllowed } from '@/features/misc/routes/NotAllowed';
import { ServicesEntry } from '@/features/services/layouts/routes/ServicesEntry';
import { usePermissions } from '@/hooks/usePermissions';
import { AdminLayout } from '@/layouts/AdminLayout';
import { MasterLayout } from '@/layouts/MasterLayout/MasterLayout';
import { lazyImport } from '@/utils/lazyImport';

import { getIrmDeniedRedirect } from './irmDeniedRedirect';
import { LegacyPrefixRedirect } from './LegacyPrefixRedirect';

const { AdminAIRoutes } = lazyImport(() => import(`@/features/adminAI`), `AdminAIRoutes`);
const { WorkrRoutes } = lazyImport(() => import(`@/features/workr`), `WorkrRoutes`);
const { Landing } = lazyImport(() => import(`@/features/misc`), `Landing`);
const { CompanyRoutes } = lazyImport(() => import(`@/features/company`), `CompanyRoutes`);
const { AgendaRoutes } = lazyImport(() => import(`@/features/agenda`), `AgendaRoutes`);
const { UsersRoutes } = lazyImport(() => import(`@/features/users`), `UsersRoutes`);
const { StatusRoutes } = lazyImport(() => import(`@/features/status`), `StatusRoutes`);
const { ProfileRoutes } = lazyImport(() => import(`@/features/profile`), `ProfileRoutes`);
const { IrmRoutes } = lazyImport(() => import(`@/features/irm`), `IrmRoutes`);

const { home, admin, cms, irm, webcast, ai, profile, email, services, calendar, automation } =
  ROUTES.children;

const webcastLink = ROUTES.getLink(`webcast`);
const videoconferenceLink = ROUTES.getLink(`irm.videoconference`);
const aiLink = ROUTES.getLink(`ai`);
const astridLink = ROUTES.getLink(`irm.astrid`);

const App = () => {
  const { isLoading, isError } = useMyCompanies({ config: { onError: undefined } });

  const { pathname } = useLocation();
  const { hasRoute, startingPage } = usePermissions();
  const isAccessAllowed = hasRoute(pathname);
  const irmBase = irm.url;
  const irmDeniedTarget = pathname.startsWith(irmBase) ? getIrmDeniedRedirect(hasRoute) : undefined;
  const redirectTarget = !isAccessAllowed ? irmDeniedTarget ?? startingPage : undefined;

  if (isLoading) {
    return (
      <div className="u-flex u-align-center u-justify-center" style={{ height: `100vh` }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <MasterLayout>
      <Suspense
        fallback={
          <Space align="center" className="u-flex u-justify-center u-w-full u-h-full">
            <Spin size="default" />
          </Space>
        }
      >
        {isAccessAllowed}
        {isAccessAllowed ? (
          <Outlet />
        ) : isError ? (
          <Result status="500" title="500" subTitle="Desculpe, algo deu errado." />
        ) : redirectTarget && redirectTarget !== pathname ? (
          <Navigate to={redirectTarget} replace />
        ) : (
          <NotAllowed noExtra />
        )}
      </Suspense>
    </MasterLayout>
  );
};

export const protectedRoutes = [
  {
    element: <App />,
    children: [
      {
        path: home.base,
        exact: true,
        element: <Landing />,
      },
      {
        path: admin.base,
        element: <AdminLayout />,
        children: [
          { path: admin.children?.company.base, element: <CompanyRoutes /> },
          { path: admin.children?.users.base, element: <UsersRoutes /> },
          { path: admin.children?.status.base, element: <StatusRoutes /> },
          { path: admin.children?.ai.base, element: <AdminAIRoutes /> },
          { path: admin.children?.profile.base, element: <ProfileRoutes /> },
          { path: admin.children?.agenda.base, element: <AgendaRoutes /> },
          { path: admin.children?.workr.base, element: <WorkrRoutes /> },
        ],
      },
      {
        path: cms.base,
        element: <CmsRoutes />,
      },
      {
        path: irm.base,
        element: <IrmRoutes />,
      },
      {
        path: webcast.base,
        element: <LegacyPrefixRedirect from={webcastLink} to={videoconferenceLink} />,
      },
      {
        path: ai.base,
        element: <LegacyPrefixRedirect from={aiLink} to={astridLink} />,
      },
      {
        path: services.base,
        element: <ServicesEntry />,
      },
      {
        path: automation.base,
        element: <AutomationRoutes />,
      },
      {
        path: calendar.base,
        element: <CalendarRoutes />,
      },
      {
        path: profile.base,
        element: <ProfileRoutes />,
      },
      {
        path: email.base,
        element: <EmailRoutes />,
      },
    ],
  },
];
